// The spike's screens on @rpg/ui (gate A4.2): title, loading, pause, game over, settings, controls (rebinding),
// save and load slots, the sync conflict dialog, and the cloud-saves account (sign in and out). Every text is a string
// id from the strings table. The flow (src/boot/) decides which root screen shows; menus push onto the same stack and
// back pops them. Since the composition framework's flow stream the title, loading, pause and game-over screens are the
// front-end feature's (@rpg-unit/front-end-flow), the slot and conflict screens the save-slots feature's
// (@rpg-unit/save-slots) and the settings screen the settings feature's (@rpg-unit/settings); the spike keeps the
// controls and account screens and joins the features' menus here (none imports another).
import { Button, h, Menu, RebindPanel, Screen, Status, TextField, type RebindLabels, type RebindState, type ScreenContext, type ScreenRender, type Settings } from "@rpg/ui";
import type { SlotInfo } from "@rpg/save";
import { frontEndScreens, type MenuEntry, type PauseParams } from "@rpg-unit/front-end-flow";
import { saveSlotScreens, slotMenuEntries } from "@rpg-unit/save-slots";
import { settingsScreen } from "@rpg-unit/settings";
import type { SpikeSaves } from "../save";
import type { GameRebind } from "./rebind-model";

export type { TitleParams, PauseParams } from "@rpg-unit/front-end-flow";
export type { SlotsParams, ConflictParams } from "@rpg-unit/save-slots";

/** What the screens need from the rest of the game. */
export interface ScreenDeps {
  saves: SpikeSaves;
  settings: Settings;
  rebind: GameRebind;
  controlLabel: (control: string) => string;
}

const REBIND_LABELS: RebindLabels = {
  devices: "rebind.devices",
  device: { keyboard: "device.keyboard", gamepad: "device.gamepad", touch: "device.touch" },
  slotName: "rebind.slotName",
  waiting: "rebind.waiting",
  capture: "rebind.capture",
  cancelled: "rebind.cancelled",
  unbound: "rebind.unbound",
  conflict: "rebind.conflict",
  resetAll: "rebind.resetAll",
  bound: "rebind.bound",
};

/** Build the screens. `slots` is the cache the slot screens read (filled by `refreshSlots`). */
export function spikeScreens(d: ScreenDeps): {
  screens: Record<string, ScreenRender>;
  refreshSlots(ui: ScreenContext["ui"]): Promise<void>;
  slots(): SlotInfo[] | null;
  /** A screen was opened (pushed, or reset as the root): fresh slot data and status lines. */
  opened(id: string, ui: ScreenContext["ui"]): void;
  /** Phase D1: a pause-menu entry a scene adds (the rpg layer's inventory and skills), before Save. */
  addPauseEntry(e: MenuEntry<PauseParams>): () => void;
} {
  const rebindState: RebindState = { device: "keyboard", capturing: null, status: "" };
  // the account screen's form (kept across renders) and its status line
  const form = { email: "", password: "", busy: false, status: "" };
  // the slot and conflict screens (the slot list is read when the slot screen or the title opens)
  const slots = saveSlotScreens({ saves: d.saves, refreshOn: ["title"] });
  // the title, loading, pause and game over; Continue loads the autosave
  const front = frontEndScreens({
    continueFrom: { can: () => !!slots.slots()?.find((s) => s.key === "auto" && s.record), go: () => d.saves.loadSlot("auto").then((r) => r.ok) },
  });
  const slotEntries = slotMenuEntries({ policy: d.saves.policy, slots: slots.slots });
  front.addTitleEntry(slotEntries.titleLoad);
  front.addTitleEntry({ label: "ui.settings", action: "settings", order: 20, press: (ctx) => ctx.ui.push("settings") });
  for (const e of slotEntries.pause) front.addPauseEntry(e);
  front.addPauseEntry({ label: "ui.settings", action: "settings", order: 30, press: (ctx) => ctx.ui.push("settings") });
  front.addPauseEntry({ label: "ui.controls", action: "controls", order: 31, press: (ctx) => ctx.ui.push("controls") });
  const screens: Record<string, ScreenRender> = {
    ...front.screens,
    // the settings feature's screen (@rpg-unit/settings): the panel by the standard sections, then controls, the
    // cloud-saves account and reset
    settings: settingsScreen({
      settings: () => d.settings,
      links: [
        { label: "ui.controls", action: "controls", screen: "controls" },
        { label: "ui.account", action: "account", screen: "account" },
      ],
    }),
    controls: (ctx) =>
      h(
        Screen,
        { ctx, title: ctx.t("ui.controls"), dim: true },
        h(RebindPanel, { ctx, model: d.rebind, labels: REBIND_LABELS, state: rebindState, controlLabel: (c) => d.controlLabel(c) }),
      ),
    slots: slots.screens.slots!,
    account: (ctx) => {
      const acc = d.saves.account;
      const user = acc.user();
      const signIn = async (e: Event): Promise<void> => {
        e.preventDefault();
        if (form.busy) return;
        form.busy = true;
        form.status = ctx.t("account.signingIn");
        ctx.ui.refresh();
        const r = await acc.signIn(form.email.trim(), form.password);
        form.busy = false;
        form.password = "";
        if (r.ok) form.status = "";
        else {
          console.warn(`[ui] sign-in failed: ${r.error}`);
          form.status = ctx.t("account.failed");
        }
        ctx.ui.refresh();
      };
      const signOut = async (): Promise<void> => {
        form.busy = true;
        ctx.ui.refresh();
        try {
          await acc.signOut();
          form.status = ctx.t("account.signedOut");
        } catch {
          form.status = ctx.t("account.signOutFailed");
        }
        form.busy = false;
        ctx.ui.refresh();
      };
      const body = !acc.configured
        ? h("p", null, ctx.t("account.unavailable"))
        : user
          ? h(
              Menu,
              null,
              h("p", null, ctx.t("account.signedIn", { email: user.email ?? "" })),
              h(Button, { label: ctx.t("account.signOut"), autofocus: true, disabled: form.busy, action: "sign-out", onPress: () => void signOut() }),
            )
          : h(
              "form",
              { class: "rpg-menu", "aria-describedby": "account-intro", onSubmit: (e: Event) => void signIn(e) },
              h("p", { id: "account-intro" }, ctx.t("account.intro")),
              h(TextField, { id: "account-email", label: ctx.t("account.email"), type: "email", value: form.email, autocomplete: "email", autofocus: true, required: true, onInput: (v) => void (form.email = v) }),
              h(TextField, { id: "account-password", label: ctx.t("account.password"), type: "password", value: form.password, autocomplete: "current-password", required: true, onInput: (v) => void (form.password = v) }),
              h("button", { type: "submit", class: "rpg-btn", "data-variant": "primary", "data-action": "sign-in", disabled: form.busy }, ctx.t("account.signIn")),
            );
      return h(Screen, { ctx, title: ctx.t("ui.account"), dim: true }, body, h(Status, { text: form.status }));
    },
    conflict: slots.screens.conflict!,
  };
  const opened = (id: string, ui: ScreenContext["ui"]): void => {
    slots.opened(id, ui);
    if (id === "controls") rebindState.status = "";
    if (id === "account") form.status = "";
  };
  return { screens, refreshSlots: slots.refresh, slots: slots.slots, opened, addPauseEntry: front.addPauseEntry };
}
